import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Star, Clock, UserCheck, BookOpen, Target, Zap, MessageCircle, HelpCircle } from 'lucide-react';
import LeadForm from '@/components/LeadForm';

const WA = 'https://wa.me/919930010800?text=Hi%20Planet%20Gyaan!%20I%27d%20like%20to%20book%20a%201:1%20doubt%20clearing%20session.';
const TEL = 'tel:+919930010800';
const LOGO = 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/no8w3qt3_Untitled%20design-73.png';

const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const features = [
  { icon: UserCheck, title: 'Dedicated Tutor', desc: 'One teacher focused entirely on you. No distractions, no waiting for your turn.', detail: 'Our tutors are handpicked for their ability to explain complex concepts simply. Each session is tailored to your specific learning style and pace.' },
  { icon: Clock, title: 'Flexible Scheduling', desc: 'Book sessions at times that work for you. Weekdays, weekends, or before exams.', detail: 'Morning, evening, or weekend slots available. Reschedule up to 4 hours before your session with no penalty.' },
  { icon: BookOpen, title: 'Any Topic, Any Chapter', desc: 'Stuck on Trigonometry? Confused about Organic Chemistry? Bring any doubt.', detail: 'From basic concepts to advanced JEE/NEET level problems. No question is too simple or too complex for our tutors.' },
  { icon: Target, title: 'Exam-Focused Prep', desc: 'Get targeted help for CBSE boards, unit tests, JEE, or NEET-specific problems.', detail: 'Practice with previous year questions, mock tests, and curated problem sets designed for maximum exam readiness.' },
  { icon: Zap, title: 'Instant Clarity', desc: 'No waiting until the next class. Get your concepts cleared immediately.', detail: 'Same-day session booking available for urgent doubts. Walk out with complete understanding every single time.' },
  { icon: MessageCircle, title: 'Safe Space to Ask', desc: 'No judgement, no rush. Ask the same question twice if you need to.', detail: 'We believe every question is important. Our tutors create a comfortable environment where curiosity thrives.' },
];

const testimonials = [
  { name: 'Rohan D.', grade: 'Grade 10', text: 'The 1:1 sessions changed everything. I went from failing in Physics to scoring 88%. The teacher explained each concept until I truly understood.', score: '88%' },
  { name: 'Ananya K.', grade: 'Grade 11', text: 'Before exams, I book 1:1 sessions for my weak chapters. My JEE mock scores improved by 40 marks!', score: 'JEE' },
  { name: 'Vikram S.', grade: 'Grade 9', text: 'I was too shy to ask doubts in class. These private sessions gave me confidence. Now I actually understand Math class.', score: '91%' },
];

const DoubtClearing = () => {
  const [activeCard, setActiveCard] = useState(null);

  return (
    <>
      <Helmet>
        <title>1:1 Doubt Clearing Sessions | Planet Gyaan | Math & Science | Santacruz Mumbai</title>
        <meta name="description" content="Personalized 1:1 doubt clearing sessions in Mathematics and Science for CBSE students. Flexible scheduling, dedicated tutors, instant clarity. Planet Gyaan, Santacruz West, Mumbai." />
        <meta name="keywords" content="doubt clearing sessions, 1:1 tuition, private tuition Santacruz, math doubts, science doubts, CBSE help, Planet Gyaan" />
        <meta property="og:title" content="1:1 Doubt Clearing - Planet Gyaan" />
        <meta property="og:description" content="Clear every doubt with personalized 1:1 sessions in Math & Science." />
        <meta property="og:image" content={LOGO} />
        <link rel="canonical" href="https://planetgyaan.com/doubt-clearing" />
      </Helmet>

      {/* HERO */}
      <section data-testid="doubt-hero" className="relative min-h-[85vh] bg-[#18181B] flex items-center overflow-hidden noise-bg">
        <div className="relative z-10 container mx-auto px-6 lg:px-16 pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="inline-block text-[#E9811F] font-bold uppercase tracking-[0.2em] text-xs md:text-sm mb-6 font-body"
              >
                1:1 Private Sessions
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
                className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9]"
              >
                <span className="text-white">Clear Every</span><br/>
                <span className="text-[#E9811F]">Doubt.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
                className="text-white/60 text-base md:text-lg mt-6 max-w-md font-body leading-relaxed"
              >
                Personalized 1:1 doubt clearing sessions in Math & Science. Your pace, your questions, your understanding.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
                className="flex flex-wrap gap-4 mt-8"
              >
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="doubt-whatsapp-cta"
                  className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 transition-all">
                  <WaIcon /> Book a Session
                </a>
                <a href={TEL} data-testid="doubt-call-cta"
                  className="flex items-center gap-2 bg-[#E9811F] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 transition-all">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </motion.div>
            </div>
            {/* Interactive cards instead of static image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex flex-col gap-4"
            >
              {[
                { q: 'Why is the sky blue?', a: 'Rayleigh scattering of sunlight...', subject: 'Physics' },
                { q: 'Derivative of sin(x)?', a: 'cos(x) - by first principles', subject: 'Math' },
                { q: 'How does photosynthesis work?', a: '6CO\u2082 + 6H\u2082O \u2192 C\u2086H\u2081\u2082O\u2086 + 6O\u2082', subject: 'Biology' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + i * 0.2, duration: 0.5 }}
                  whileHover={{ scale: 1.03, x: -5 }}
                  className="glass-card rounded-xl p-5 cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#E9811F] text-xs font-bold font-body">{item.subject}</span>
                    <HelpCircle className="w-4 h-4 text-white/30 group-hover:text-[#E9811F] transition-colors" />
                  </div>
                  <p className="text-white font-body text-sm font-medium">{item.q}</p>
                  <p className="text-white/40 text-xs font-body mt-1 group-hover:text-white/60 transition-colors">{item.a}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE FEATURES - Click to Expand */}
      <section data-testid="doubt-features" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">Click to Explore</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              Your Doubts, <span className="text-[#E9811F]">Our Priority</span>
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
                onClick={() => setActiveCard(activeCard === i ? null : i)}
                data-testid={`doubt-feature-${i}`}
                className={`rounded-2xl p-6 cursor-pointer transition-all border-2 ${
                  activeCard === i
                    ? 'border-[#E9811F] bg-[#E9811F]/5 shadow-[4px_4px_0px_0px_rgba(233,129,31,0.5)]'
                    : 'border-[#121212] bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-[1px] hover:-translate-y-[1px]'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                  activeCard === i ? 'bg-[#E9811F] text-white' : 'bg-[#E9811F]/10 text-[#E9811F]'
                }`}>
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-sans font-bold text-[#121212] text-base mb-2">{f.title}</h3>
                <p className="text-[#52525B] text-sm font-body leading-relaxed">{f.desc}</p>
                <AnimatePresence>
                  {activeCard === i && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="mt-4 pt-4 border-t border-[#E9811F]/20 text-[#121212] text-sm font-body leading-relaxed">{f.detail}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="doubt-testimonials" className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">Real Results</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              Students <span className="text-[#E9811F]">Love It</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                data-testid={`doubt-testimonial-${i}`}
                className="bg-white rounded-2xl p-6 border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#E9811F] text-[#E9811F]" />)}
                </div>
                <p className="text-[#121212] text-sm font-body leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <span className="block font-sans font-bold text-[#121212] text-sm">{t.name}</span>
                    <span className="block text-[#52525B] text-xs font-body">{t.grade}</span>
                  </div>
                  <span className="text-[#E9811F] font-black text-lg font-sans">{t.score}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + FORM */}
      <section data-testid="doubt-cta" className="py-16 md:py-24 bg-[#E9811F] border-y-2 border-[#121212]">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight">
                Don't Let Doubts Hold You Back
              </h2>
              <p className="text-white/80 text-base md:text-lg mt-4 font-body max-w-lg">
                Book a 1:1 session today. Contact us for pricing and availability.
              </p>
              <div className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start">
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="doubt-cta-whatsapp"
                  className="flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#121212] transition-all">
                  <WaIcon /> WhatsApp Us
                </a>
                <a href={TEL} data-testid="doubt-cta-call"
                  className="flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#FAFAFA] transition-all border-2 border-[#121212]">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
              <Link to="/" data-testid="doubt-back-home" className="inline-flex items-center gap-1 text-white/70 text-sm font-body mt-6 hover:text-white transition-colors">
                <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
              </Link>
            </div>
            <LeadForm variant="dark" />
          </div>
        </div>
      </section>
    </>
  );
};

export default DoubtClearing;
